<template>
  <q-dialog v-model="visible" persistent>
    <q-card class="container">
      <div class="modal-header">
        <q-card-section class="header">
          <div class="header-content-wrapper">
            <div class="header-title">{{ residueData.nome }}</div>
            <q-btn flat round color="white" icon="close" @click="onClose" class="header-close-btn" />
          </div>
        </q-card-section>
      </div>

      <div class="modal-body">
        <div class="content">
          <div v-if="loading" class="loading-section">
            <q-inner-loading :showing="loading">
              <q-spinner-gears size="80px" color="primary" />
            </q-inner-loading>
          </div>

          <div v-if="hasError" class="text-negative text-center q-pa-md">
            {{ errorMessage }}
          </div>

          <div v-else-if="!loading">
            <!-- Container principal com duas colunas -->
            <div class="main-content-row">
              <!-- Row da data no topo -->
              <div class="data-hoje-row">
                <div class="div-subtitulo">{{ currentDateLabel }}</div>
              </div>

              <!-- Container das duas colunas -->
              <div class="columns-container">
                <!-- Coluna da esquerda: Gifs e informações do dia atual -->
                <div class="left-column">
                  <div class="container-coletas-vertical">
                    <div class="coletas-feitas-block">
                      <div class="text-h6 q-mt-sm">{{ $gettext('INDUSTRIAL_MAP_COLLECTIONS_MADE') }}</div>
                      <TruckIcon />
                      <div class="text-h4 text-weight-bold text-modal-body">{{ currentStats.coletasFeitas }}</div>
                    </div>
                    <div class="qtd-coletada-block">
                      <div class="text-h6 q-mt-sm">{{ $gettext('INDUSTRIAL_MAP_QUANTITY_COLLECTED') }}</div>
                      <TrashIcon />
                      <div class="text-h4 text-weight-bold text-modal-body">{{ currentStats.qtdColetada }}kg</div>
                    </div>
                  </div>
                </div>

                <!-- Coluna da direita: Acordeões de todas as datas -->
                <div class="right-column">
                <div class="q-mt-md">
                  <!-- Todos os acordeões ordenados por data -->
                  <q-expansion-item
                    v-for="(group, index) in todosAcordeonsOrdenados"
                    :key="index"
                    :label="group.label"
                    class="coletas-realizadas-header q-mb-sm"
                    header-class="text-white"
                    expand-icon-class="text-white"
                    @show="onAccordionOpen(group.type, group.originalIndex)"
                    @hide="onAccordionClose"
                  >
                    <q-card>
                      <q-card-section>
                        <div class="text-h6 q-mb-sm">{{$gettext('INDUSTRIAL_MAP_COLLECTIONS') }}</div>
                        <q-list class="text-dark">
                          <q-item v-for="(coleta, cIndex) in group.coletas" :key="cIndex" class="collection-detail-item collection-line">
                            <div class="collection-col1">{{ coleta.id }} - {{ coleta.placa }} {{ coleta.horario }}</div>
                            <div class="collection-col2">{{ coleta.observacao }}</div>
                          </q-item>
                        </q-list>
                      </q-card-section>
                    </q-card>
                  </q-expansion-item>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <q-card-actions align="center" class="footer">
          <q-btn label="FECHAR" color="grey-7" class="btn-fechar" @click="onClose" />
        </q-card-actions>
      </div>
    </q-card>
  </q-dialog>
</template>

<script>
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import moment from 'moment'
import { IndustrialMapRepository } from 'src/modules/industry/industrial-map/IndustrialMapRepository.js'
import TruckIcon from 'src/components/industrialMap/icons/TruckIcon.vue'
import TrashIcon from 'src/components/industrialMap/icons/TrashIcon.vue'

@Component({
  name: 'ModalDetalhesResidue',
  components: {
    TruckIcon,
    TrashIcon
  }
})
class ModalDetalhesResidue extends Vue {
  @Prop({ type: Boolean, default: false })
  value

  @Prop({ type: Object, default: () => ({}) })
  residue

  loading = false
  hasError = false
  errorMessage = ''
  residueDetails = null
  openAccordionType = null // 'today', 'other', ou null
  openAccordionIndex = null // index para acordeões de outras datas

  get visible () {
    return this.value
  }

  set visible (val) {
    this.$emit('input', val)
  }

  get residueData () {
    return this.residueDetails || {
      nome: (this.residue && this.residue.nome) || 'Nome Resíduo'
    }
  }

  get dashboardStats () {
    return (this.residueDetails && this.residueDetails.dashboardStats) || {
      coletasFeitas: 0,
      qtdColetada: '0'
    }
  }

  get coletasDoDia () {
    return (this.residueDetails && this.residueDetails.coletasDoDia) || []
  }

  get coletasAgrupadas () {
    return (this.residueDetails && this.residueDetails.coletasAgrupadas) || []
  }

  get todosAcordeonsOrdenados () {
    const acordeonsArray = []

    // Adicionar acordeão de hoje
    if (this.residueDetails) {
      const hoje = moment().format('DD/MM/YYYY')
      acordeonsArray.push({
        label: `${hoje} - ${this.coletasDoDia.length} coletas (${this.getTodayQuantity()}kg)`,
        coletas: this.coletasDoDia,
        stats: this.dashboardStats,
        type: 'today',
        originalIndex: null,
        dataOriginal: moment().format('DD/MM/YYYY HH:mm')
      })
    }

    // Adicionar outros acordeões
    this.coletasAgrupadas.forEach((group, index) => {
      acordeonsArray.push({
        label: group.label,
        coletas: group.coletas,
        stats: group.stats,
        type: 'other',
        originalIndex: index,
        dataOriginal: group.dataOriginal
      })
    })

    // Ordenar todos por data (mais recente primeiro)
    return acordeonsArray.sort((a, b) => {
      const dataA = moment(a.dataOriginal, 'DD/MM/YYYY HH:mm')
      const dataB = moment(b.dataOriginal, 'DD/MM/YYYY HH:mm')
      return dataB.diff(dataA) // B - A = ordem decrescente (mais recente primeiro)
    })
  }

  get currentDateLabel () {
    if (this.openAccordionType === 'today') {
      return `Hoje - ${this.formatDate(new Date())}`
    } else if (this.openAccordionType === 'other' && this.openAccordionIndex !== null) {
      const group = this.coletasAgrupadas[this.openAccordionIndex]
      if (group) {
        // Extrair apenas a data do label (antes do " - ")
        const dateOnly = group.label.split(' - ')[0]
        return dateOnly
      }
    }
    // Padrão: hoje
    return `Hoje - ${this.formatDate(new Date())}`
  }

  get currentStats () {
    if (this.openAccordionType === 'today') {
      return this.dashboardStats
    } else if (this.openAccordionType === 'other' && this.openAccordionIndex !== null) {
      const group = this.coletasAgrupadas[this.openAccordionIndex]
      if (group && group.stats) {
        return group.stats
      }
    }
    // Padrão: stats de hoje
    return this.dashboardStats
  }

  formatDate (date) {
    return moment(date).format('DD/MM/YYYY')
  }

  getTodayQuantity () {
    // Buscar a quantidade de hoje nos dados dos registros
    const hoje = moment().format('YYYY-MM-DD')
    const registros = (this.residueDetails && this.residueDetails.registrosOriginais) || []
    const dadosHoje = registros.find(r => {
      const dataRegistro = moment(r.scheduledDate, 'DD/MM/YYYY HH:mm').format('YYYY-MM-DD')
      return dataRegistro === hoje
    })
    return dadosHoje ? dadosHoje.totalQuantityByDate || '0' : '0'
  }

  onClose () {
    this.visible = false
    this.$emit('close')
  }

  onAccordionOpen (type, index = null) {
    this.openAccordionType = type
    this.openAccordionIndex = index
  }

  onAccordionClose () {
    this.openAccordionType = null
    this.openAccordionIndex = null
  }

  @Watch('visible')
  async onModalOpen (isVisible) {
    if (isVisible && this.residue?.product) {
      // Reset accordion state
      this.openAccordionType = null
      this.openAccordionIndex = null
      await this.loadResidueDetails()
    } else if (isVisible) {
    }
  }

  async loadResidueDetails () {
    try {
      this.loading = true
      const productId = this.residue.product

      if (!productId) {
        throw new Error('ID do produto não encontrado')
      }

      const response = await IndustrialMapRepository.findResidue(productId)

      if (response.result === 'success' || (response.registros && response.totalCount !== undefined)) {
        if (response.registros && response.registros.length > 0) {
          // Processar dados do backend para formato esperado pelo modal
          this.residueDetails = this.processResidueData(response)
          this.hasError = false
          this.errorMessage = ''
        } else {
          // Caso não tenha registros, exibir modal com dados zerados
          this.residueDetails = this.processEmptyData()
          this.hasError = false
          this.errorMessage = ''
        }
      } else {
        this.hasError = true
        this.errorMessage = 'Não foi possível exibir o registro.'
        console.error('Erro ao carregar detalhes do resíduo: Resposta inválida', response)
      }
    } catch (error) {
      this.hasError = true
      this.errorMessage = 'Não foi possível exibir o registro. Erro: ' + error.message
      console.error('Erro ao carregar detalhes do resíduo:', error)
      this.$q.notify({
        type: 'negative',
        message: 'Erro ao carregar detalhes do resíduo'
      })
    } finally {
      this.loading = false
    }
  }

  /**
   * Conta apenas as coletas com status CONFIRMADO e PESO CONFIRMADO
   * @param {Array} collections - Array de coletas
   * @returns {Number} Quantidade de coletas confirmadas
   */
  countConfirmedCollections (collections) {
    if (!collections || !Array.isArray(collections)) return 0

    return collections.filter(c =>
      c.taskStatusType === 'COMPLETADA' ||
      c.taskStatusType === 'PESAGEM PENDENTE' ||
      c.taskStatusType === 'CONFIRMADO'
    ).length
  }

  /**
   * Processa os dados do backend para o formato esperado pelo modal
   * @param {Object} response - Resposta do findResidue
   * @returns {Object} Dados formatados para o modal
   */
  processResidueData (response) {
    const registros = response.registros || []
    const hoje = moment().format('YYYY-MM-DD')

    // Dados agregados totais (pegar do primeiro registro)
    const totalStats = registros.length > 0 ? {
      totalCollections: registros[0].totalCollections || 0,
      totalQuantity: registros[0].totalQuantity || '0'
    } : { totalCollections: 0, totalQuantity: '0' }

    // Encontrar dados de hoje
    const dadosHoje = registros.find(r => {
      const dataRegistro = moment(r.scheduledDate, 'DD/MM/YYYY HH:mm').format('YYYY-MM-DD')
      return dataRegistro === hoje
    })

    // Coletas de hoje
    const coletasDoDia = dadosHoje ? this.formatCollections(dadosHoje.collections || []) : []

    // Stats do dashboard (apenas dados de hoje, não totais)
    // coletasFeitas = apenas coletas com status CONFIRMADO e PESO CONFIRMADO
    const dashboardStats = {
      coletasFeitas: dadosHoje ? this.countConfirmedCollections(dadosHoje.collections || []) : 0,
      qtdColetada: dadosHoje ? dadosHoje.totalQuantityByDate || '0' : '0'
    }

    // Agrupar coletas por data (agora incluindo todas as datas, menos hoje que será tratado separadamente)
    const coletasAgrupadas = registros
      .filter(r => {
        const dataRegistro = moment(r.scheduledDate, 'DD/MM/YYYY HH:mm').format('YYYY-MM-DD')
        return dataRegistro !== hoje // Excluir hoje pois será tratado no computed property
      })
      .map(r => ({
        label: `${r.scheduledDate.split(' ')[0]} - ${r.totalCollectionsByDate} coletas (${r.totalQuantityByDate}kg)`,
        coletas: this.formatCollections(r.collections || []),
        stats: {
          coletasFeitas: this.countConfirmedCollections(r.collections || []), // Apenas coletas confirmadas
          qtdColetada: r.totalQuantityByDate || '0'
        },
        dataOriginal: r.scheduledDate // Manter a data original para ordenação
      }))

    return {
      nome: this.residue.nome || 'Nome Resíduo',
      dashboardStats,
      coletasDoDia,
      coletasAgrupadas,
      registrosOriginais: registros // Armazenar para uso em outros métodos
    }
  }

  /**
   * Formata as coletas para exibição no modal
   * @param {Array} collections - Array de coletas do backend
   * @returns {Array} Coletas formatadas
   */
  formatCollections (collections) {
    return collections.map(c => ({
      id: c.id || '',
      placa: c.plate || 'SEM PLACA',
      horario: c.scheduledDate ? moment(c.scheduledDate, 'DD/MM/YYYY HH:mm').format('HH:mm') : '',
      observacao: c.taskStatusType || ''
    }))
  }

  /**
   * Processa dados vazios quando não há registros
   * @returns {Object} Dados vazios formatados para o modal
   */
  processEmptyData () {
    return {
      nome: this.residue.nome || 'Nome Resíduo',
      dashboardStats: {
        coletasFeitas: 0,
        qtdColetada: '0'
      },
      coletasDoDia: [],
      coletasAgrupadas: []
    }
  }
}

export default ModalDetalhesResidue
</script>

<style lang="stylus" scoped>
.container
  width: 920px
  max-width: 920px
  height: 640px
  max-height: 640px
  border-radius: 10px
  padding-top: 16px
  background: white
  display: flex
  flex-direction: column
  gap: 20px
  box-shadow: 0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px rgba(0,0,0,0.14), 0 1px 10px rgba(0,0,0,0.12)

.modal-header
  flex-shrink: 0;

.header
  height: 50px
  padding: 8px 20px
  border-radius: 4px
  color: white !important
  flex-shrink: 0
  background-color: #1E4971

.coletas-realizadas-header
  background-color: #0F5295

.header-content-wrapper
  width: 856px
  height: 34px
  gap: 20px
  display: flex
  align-items: center
  justify-content: space-between
  margin: 0 auto

.header-title
  height: 34px
  font-family: Roboto
  font-weight: 700
  font-style: Bold
  font-size: 28px
  line-height: 34px
  flex-grow: 1

.header-close-btn
  flex-shrink: 0
  cursor: pointer; /* Add cursor pointer for better UX */
  margin: 0;

.modal-body
  height: 544px; /* Adjusted height */
  overflow-y: auto;
  padding: 0px 16px 0px 0px; /* Add padding to right */

.content
  gap: 8px
  display: flex
  flex-direction: column
  flex-grow: 0
  padding: 0px 0px 0px 16px; /* Add padding to left */
  width: 888px;

.modal-footer
  flex-shrink: 0;

.footer
  height: 60px
  gap: 20px
  padding: 12px 0; /* Add padding to top and bottom */
  background: #FFFFFF /* Change background color */
  display: flex
  justify-content: center /* Center the button horizontally */
  align-items: center
  flex-shrink: 0

.data-hoje-row
  width: 100%
  margin-bottom: 16px
  flex-shrink: 0

.main-content-row
  width: 888px;
  gap: 16px;
  angle: 0 deg;
  opacity: 1;
  border-radius: 8px;
  padding: 16px;
  border-width: 1px;
  display: flex
  flex-direction: column
  flex-grow: 1
  overflow: hidden
  background: #FCFCFC
  border: 1px solid #E0E0E0

.columns-container
  display: flex
  gap: 16px
  flex-grow: 1
  overflow: hidden

.left-column
  width: 298px;
  height: 390px;
  gap: 85px;
  angle: 0deg;
  opacity: 1;
  display: flex;
  flex-direction: column;

.right-column
  width: 448px
  flex-grow: 1

.container-coletas-vertical
  display: flex
  flex-direction: column
  gap: 60px;
  flex-grow: 1

.coletas-hoje-info
  border-radius: 8px
  padding: 12px
  border-left: 3px solid #0679CC
  background: #FFFFFF
  max-height: 200px

.container1
  width: 888px
  min-height: 362px
  gap: 20px
  display: flex
  flex-direction: column
  flex-shrink: 0

.container2
  width: 888px
  gap: 8px
  display: flex
  flex-direction: column
  flex-grow: 0
  overflow-y: auto

.text-modal-body
  color: #31CCCB

.div-inside-container1
  width: 888px
  gap: 16px
  border-width: 1px
  border-radius: 8px
  padding: 16px
  background: #FCFCFC
  border: 1px solid #E0E0E0
  display: flex
  flex-direction: column
  flex-shrink: 0

.div-subtitulo
  font-family: Roboto;
  font-weight: 600;
  font-style: SemiBold;
  font-size: 18px;
  leading-trim: NONE;
  line-height: 100%;
  letter-spacing: 0%;
  color: #707070;

.container-coletas
  width: 856px
  height: 128px
  gap: 16px
  display: flex
  flex-direction: row
  justify-content: space-between
  flex-shrink: 0

.coletas-feitas-block, .qtd-coletada-block
  width: 100%
  height: 140px
  padding: 20px
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  border-radius: 8px
  flex-shrink: 0

.btn-fechar
  width 120px

.loading-section
  min-height 200px
  display flex
  align-items center
  justify-content center
  padding-top 40px
  padding-bottom 40px
  flex-grow: 1

.collection-detail-item.collection-line
  width: 542px !important
  min-height: 26px !important
  padding: 0px !important
  margin: 0 auto
  margin-bottom: 8px
  border-radius 4px
  display: flex
  align-items: center
  gap: 16px
  flex-shrink: 0

.collection-col1
  width: 260px !important
  min-height: 26px !important
  padding: 8px !important
  flex-shrink: 0
  line-height: 1.5
  display: flex
  flex-direction: row
  background-color: #f2f2f2
  justify-content: space-between
  border-radius: 4px

.collection-col2
  width: 240px !important
  min-height: 26px !important
  padding: 8px !important
  flex-shrink: 0
  background-color: #f2f2f2
  border-radius: 4px
</style>
